/**
 * Primitive UI components migrated from designv2/src/ui.jsx.
 *
 * STATUS: scaffold. The signatures match the prototype; the full bodies
 * (with all the inline-style refinement and behavior) are converted
 * directly from `designv2/src/ui.jsx` in the per-file conversion pass.
 * For now, these render plausible placeholders so the rest of the app
 * can compose against them without TS errors.
 */
import {
  type CSSProperties,
  type FC,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import type { Person, SysRole } from "@shared/types";
import { DatePicker } from "./cells";
import { useC } from "./theme";
import { DENSITIES } from "./tokens";

export { ThemeCtx, useC } from "./theme";

export type Tone =
  | "blue"
  | "navy"
  | "neutral"
  | "green"
  | "yellow"
  | "red"
  | "violet"
  | "pink"
  | "slate"
  | "y"
  | "g";

export const Badge: FC<PropsWithChildren<{ tone?: Tone }>> = ({ children, tone = "blue" }) => {
  const { C } = useC();
  const colors: Record<Tone, { c: string; bg: string; bd: string }> = {
    blue: { c: C.blue, bg: C.blueTint2, bd: C.blueTint4 },
    navy: { c: C.navy, bg: C.blueTint2, bd: C.blueTint4 },
    neutral: { c: C.slateMut, bg: C.canvas, bd: C.line },
    green: { c: C.g, bg: C.gBg, bd: C.gBd },
    yellow: { c: C.y, bg: C.yBg, bd: C.yBd },
    red: { c: C.r, bg: C.rBg, bd: C.rBd },
    violet: { c: C.blue, bg: C.blueTint2, bd: C.blueTint4 },
    pink: { c: C.r, bg: C.rBg, bd: C.rBd },
    slate: { c: C.slateMut, bg: C.canvas, bd: C.line },
    y: { c: C.y, bg: C.yBg, bd: C.yBd },
    g: { c: C.g, bg: C.gBg, bd: C.gBd },
  };
  const v = colors[tone];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: 0.25,
        background: v.bg,
        color: v.c,
        border: `1px solid ${v.bd}`,
        whiteSpace: "nowrap",
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
};

export const SBadge: FC<{ s: "active" | "planned" | "archived" | string }> = ({ s }) => {
  const map: Record<string, Tone> = { active: "green", planned: "violet", archived: "neutral" };
  const labels: Record<string, string> = {
    active: "Active",
    planned: "Planned",
    archived: "Archived",
  };
  return <Badge tone={map[s] ?? "neutral"}>{labels[s] ?? s}</Badge>;
};

export interface BtnProps extends PropsWithChildren {
  primary?: boolean;
  ghost?: boolean;
  small?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  style?: CSSProperties;
  icon?: ReactNode;
  title?: string;
}

export const Btn: FC<BtnProps> = ({
  children,
  primary,
  ghost,
  small,
  onClick,
  disabled,
  style,
  icon,
  title,
}) => {
  const { C } = useC();
  const [h, setH] = useState(false);
  const base: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: small ? "5px 10px" : "7px 14px",
    borderRadius: 6,
    fontSize: small ? 12 : 13,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    transition: "all .12s ease",
    letterSpacing: 0.1,
  };
  let skin: CSSProperties;
  if (primary)
    skin = {
      background: h ? C.blueHover : C.blue,
      color: "#fff",
      border: `1px solid ${h ? C.blueHover : C.blue}`,
    };
  else if (ghost)
    skin = {
      background: h ? C.blueTint2 : "transparent",
      color: C.blue,
      border: "1px solid transparent",
    };
  else
    skin = {
      background: h ? C.blueTint1 : C.surface,
      color: C.navy,
      border: `1px solid ${h ? C.blueTint4 : C.line}`,
    };
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{ ...base, ...skin, ...style }}
      title={title}
    >
      {icon}
      {children}
    </button>
  );
};

export const Av: FC<{ name: string; sz?: number; tone?: "solid" | "tint" }> = ({
  name,
  sz = 28,
  tone,
}) => {
  const { C } = useC();
  const initials = (name || "?")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const solid = tone === "solid";
  return (
    <span
      style={{
        width: sz,
        height: sz,
        borderRadius: "50%",
        background: solid ? C.blue : C.blueTint2,
        color: solid ? "#fff" : C.blue,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: sz * 0.38,
        fontWeight: 700,
        flexShrink: 0,
        border: solid ? "none" : `1px solid ${C.blueTint4}`,
      }}
    >
      {initials}
    </span>
  );
};

export const Card: FC<
  PropsWithChildren<{
    style?: CSSProperties;
    onClick?: () => void;
    hover?: boolean;
    pad?: number | string;
  }>
> = ({ children, style, onClick, hover, pad }) => {
  const { C } = useC();
  const [h, setH] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hover && setH(true)}
      onMouseLeave={() => hover && setH(false)}
      style={{
        background: C.surface,
        borderRadius: 10,
        border: `1px solid ${hover && h ? C.blueTint4 : C.line}`,
        boxShadow: hover && h ? C.shM : C.sh,
        transition: "all .15s ease",
        ...(hover ? { cursor: "pointer" } : {}),
        ...(pad !== undefined ? { padding: pad } : {}),
        ...style,
      }}
    >
      {children}
    </div>
  );
};

/**
 * Hover-revealed help icon for surfaces where a field's nuance won't fit
 * inline (long explanations, gotchas, examples). Renders a circled "i"
 * glyph; on hover or keyboard focus a positioned tooltip slides in next
 * to it. Use as a label decoration:
 *
 *   <Field label={<>Code <InfoIcon tooltip="..." /></>}>
 *
 * Or anywhere inline as a standalone affordance.
 *
 * Tooltip content is a ReactNode — plain strings get wrapped with the
 * default tooltip-body styling; richer nodes render as-is.
 *
 * Positioning: defaults to right-of-icon. If the tooltip would clip the
 * right edge of the viewport the component flips to left-of-icon at
 * render time. Width is capped at 320px so long copy wraps naturally.
 */
export const InfoIcon: FC<{
  tooltip: ReactNode;
  /** Accessible name for screen readers. Defaults to "More information". */
  ariaLabel?: string;
  /**
   * `wide` (default) — 300px fixed width, suitable for paragraphs.
   * `compact` — sizes to content (max ~240px), suitable for short label:value
   * lists like "Project: €rate/h". Use compact whenever content is just a few
   * short lines; wide whenever the content has prose.
   */
  variant?: "wide" | "compact";
}> = ({ tooltip, ariaLabel = "More information", variant = "wide" }) => {
  const { C } = useC();
  const [open, setOpen] = useState(false);
  // Tooltip rendered via portal so it escapes any modal `overflow: auto`
  // container. Position is viewport-fixed and recomputed from the trigger's
  // bounding rect on open.
  const [pos, setPos] = useState<{ top: number; left: number; placement: "below" | "above" }>({
    top: 0,
    left: 0,
    placement: "below",
  });
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const tooltipId = useId();
  const TOOLTIP_W = variant === "compact" ? 240 : 300;
  const VIEWPORT_MARGIN = 8;
  useEffect(() => {
    if (!open) return;
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const estTooltipH = 150;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement = spaceBelow < estTooltipH ? "above" : "below";
    const desiredLeft = rect.left;
    const overshoot = desiredLeft + TOOLTIP_W + VIEWPORT_MARGIN - window.innerWidth;
    const shift = overshoot > 0 ? -overshoot : 0;
    setPos({
      top: placement === "below" ? rect.bottom + 6 : rect.top - 6,
      left: desiredLeft + shift,
      placement,
    });
  }, [open, TOOLTIP_W]);
  // Escape-to-close + click-outside-to-close. Click toggle on the button
  // covers touch devices (mouseenter never fires there).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (!triggerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);
  return (
    <span
      ref={triggerRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
        marginLeft: 4,
        transform: "translateY(-0.5px)",
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        style={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          border: "none",
          background: C.slateMut,
          color: C.surface,
          fontSize: 10,
          fontWeight: 700,
          fontFamily: "'Source Sans Pro', system-ui, sans-serif",
          fontStyle: "normal",
          cursor: "help",
          padding: 0,
          lineHeight: 1,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          textTransform: "none",
          letterSpacing: 0,
        }}
      >
        i
      </button>
      {open &&
        createPortal(
          <span
            id={tooltipId}
            role="tooltip"
            style={{
              position: "fixed",
              ...(pos.placement === "below"
                ? { top: pos.top }
                : { bottom: window.innerHeight - pos.top }),
              left: pos.left,
              zIndex: 1000,
              ...(variant === "compact"
                ? { width: "max-content", maxWidth: TOOLTIP_W }
                : { width: TOOLTIP_W }),
              padding: "8px 10px",
              borderRadius: 6,
              background: C.surface,
              color: C.slate,
              border: `1px solid ${C.line}`,
              fontSize: 11.5,
              lineHeight: 1.5,
              fontWeight: 400,
              letterSpacing: 0,
              textTransform: "none",
              boxShadow: "0 6px 20px rgba(20, 30, 60, 0.15)",
              whiteSpace: "normal",
              pointerEvents: "none",
            }}
          >
            {tooltip}
          </span>,
          document.body,
        )}
    </span>
  );
};

export const Field: FC<PropsWithChildren<{ label?: ReactNode; style?: CSSProperties }>> = ({
  label,
  children,
  style,
}) => {
  const { C } = useC();
  return (
    <div style={{ marginBottom: 12, ...style }}>
      {label != null && (
        // A `<span>`, not `<label>`. Nothing in the codebase sets `htmlFor`
        // so the previous `<label>` was decorative-only — and using a real
        // `<label>` element would forward clicks on any nested interactive
        // descendant (e.g. an InfoIcon `<button>`) to the field's input,
        // popping up the keyboard / focus-stealing on touch devices.
        <span
          style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            color: C.slateMut,
            marginBottom: 4,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {label}
        </span>
      )}
      {children}
    </div>
  );
};

/**
 * Frontend-only table sort.
 *
 * `useTableSort` is a small state machine over (sortKey, direction) with
 * a three-state toggle: asc → desc → unsorted. The hook returns a `sort`
 * helper that wraps a stable Intl.Collator sort over a row array given
 * an accessor map.
 *
 * Usage:
 *   type K = "name" | "code" | "budget";
 *   const accessors: Record<K, (r: Row) => string | number> = {
 *     name: (r) => r.name,
 *     code: (r) => r.code,
 *     budget: (r) => r.budget,
 *   };
 *   const { sort, isActive, ariaSortFor, toggle, indicator } =
 *     useTableSort<Row, K>(accessors);
 *   const rows = sort(filtered);
 *   ...
 *   <SortableHeader label="Name" sortKey="name" onSort={() => toggle("name")}
 *     active={isActive("name")} indicator={indicator("name")}
 *     ariaSort={ariaSortFor("name")} TH={TH} C={C} />
 *
 * Sort is stable (preserves insertion order on equal keys). String keys
 * compare via `Intl.Collator(undefined, { sensitivity: "base" })`; numeric
 * keys compare via subtraction.
 */
export function useTableSort<T, K extends string>(
  accessors: Record<K, (row: T) => string | number | null | undefined>,
) {
  const [sortKey, setSortKey] = useState<K | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc" | null>(null);
  function toggle(key: K): void {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortKey(null);
      setSortDir(null);
    }
  }
  function isActive(key: K): boolean {
    return sortKey === key && sortDir !== null;
  }
  function indicator(key: K): string {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ↑" : sortDir === "desc" ? " ↓" : "";
  }
  function ariaSortFor(key: K): "ascending" | "descending" | "none" {
    if (sortKey !== key || !sortDir) return "none";
    return sortDir === "asc" ? "ascending" : "descending";
  }
  function sort(rows: T[]): T[] {
    if (!sortKey || !sortDir) return rows;
    const collator = new Intl.Collator(undefined, { sensitivity: "base" });
    const accessor = accessors[sortKey];
    const cmp = (a: T, b: T): number => {
      const av = accessor(a);
      const bv = accessor(b);
      // Push null/undefined to the end on asc, top on desc — match what
      // most spreadsheet conventions do.
      if (av == null && bv == null) return 0;
      if (av == null) return sortDir === "asc" ? 1 : -1;
      if (bv == null) return sortDir === "asc" ? -1 : 1;
      let c: number;
      if (typeof av === "number" && typeof bv === "number") {
        c = av - bv;
      } else {
        c = collator.compare(String(av), String(bv));
      }
      return sortDir === "asc" ? c : -c;
    };
    // Spread to preserve insertion-order tiebreaker (Array.sort is stable).
    return [...rows].sort(cmp);
  }
  return { sort, toggle, isActive, indicator, ariaSortFor, sortKey, sortDir };
}

/**
 * Sortable table header cell. Wraps the label in a real `<button>` so
 * keyboard / screen-reader users get an interactive element; `<th>`
 * carries `aria-sort` so the current state is announced. Active column
 * renders bold + navyDeep. Pair with `useTableSort` for state.
 *
 * `sortable={false}` (or omitted on a regular `<th>`) leaves the column
 * non-sortable — use a plain `<th>` for those.
 */
export const SortableHeader: FC<{
  label: string;
  align?: "left" | "center" | "right";
  active: boolean;
  indicator: string;
  ariaSort: "ascending" | "descending" | "none";
  onSort: () => void;
  TH: CSSProperties;
  minWidth?: number | string;
  width?: number | string;
}> = ({ label, align = "left", active, indicator, ariaSort, onSort, TH, minWidth, width }) => {
  const { C } = useC();
  return (
    <th
      style={{
        ...TH,
        textAlign: align,
        padding: 0,
        ...(minWidth !== undefined ? { minWidth } : {}),
        ...(width !== undefined ? { width } : {}),
      }}
      aria-sort={ariaSort}
      scope="col"
    >
      <button
        type="button"
        onClick={onSort}
        title="Click to sort"
        style={{
          display: "block",
          width: "100%",
          padding: TH.padding,
          textAlign: align,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          userSelect: "none",
          fontSize: TH.fontSize,
          textTransform: TH.textTransform,
          letterSpacing: TH.letterSpacing,
          fontWeight: active ? 700 : (TH.fontWeight ?? 500),
          color: active ? C.navyDeep : TH.color,
          fontFamily: TH.fontFamily,
        }}
      >
        {label}
        <span aria-hidden="true">{indicator}</span>
      </button>
    </th>
  );
};

export const Input: FC<{
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  suffix?: ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  "aria-label"?: string;
  /** Render with a red border to signal a validation error. */
  invalid?: boolean;
}> = ({
  value,
  onChange,
  type = "text",
  placeholder,
  suffix,
  disabled,
  readOnly,
  min,
  max,
  step,
  "aria-label": ariaLabel,
  invalid,
}) => {
  const { C } = useC();
  const [f, setF] = useState(false);
  const borderColor = invalid ? C.r : f ? C.blue : C.line;
  return (
    <div style={{ position: "relative" }}>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        min={min}
        max={max}
        step={step}
        aria-label={ariaLabel}
        onFocus={() => setF(true)}
        onBlur={() => setF(false)}
        style={{
          width: "100%",
          padding: "7px 11px",
          paddingRight: suffix ? 38 : 11,
          borderRadius: 6,
          border: `1px solid ${borderColor}`,
          outline: "none",
          fontSize: 13,
          color: disabled || readOnly ? C.slateMut : C.navy,
          background: disabled || readOnly ? C.canvas : C.surface,
          boxSizing: "border-box",
        }}
      />
      {suffix != null && (
        <span
          style={{
            position: "absolute",
            right: 11,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 11,
            color: C.slateMut,
            pointerEvents: "none",
          }}
        >
          {suffix}
        </span>
      )}
    </div>
  );
};

export const Sel: FC<{
  value: string;
  onChange: (v: string) => void;
  options: Array<{ v: string; l: string }>;
  /** Render with a red border to signal a validation error. Mirrors Input. */
  invalid?: boolean;
}> = ({ value, onChange, options, invalid }) => {
  const { C } = useC();
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        padding: "7px 11px",
        borderRadius: 6,
        border: `1px solid ${invalid ? C.r : C.line}`,
        fontSize: 13,
        color: C.navy,
        background: C.surface,
        outline: "none",
        boxSizing: "border-box",
      }}
    >
      {options.map((o) => (
        <option key={o.v} value={o.v}>
          {o.l}
        </option>
      ))}
    </select>
  );
};

const escStack: Array<{ current: () => void }> = [];
let escListener: ((e: KeyboardEvent) => void) | null = null;

function ensureEscListener(): void {
  if (escListener) return;
  escListener = (e: KeyboardEvent) => {
    if (e.key !== "Escape") return;
    const top = escStack[escStack.length - 1];
    if (!top) return;
    // Browsers consume Esc to close native pickers (date/select) before it bubbles —
    // first Esc closes the picker, second Esc closes the Modal. Intentional.
    e.stopImmediatePropagation();
    top.current();
  };
  document.addEventListener("keydown", escListener);
}

function teardownEscListenerIfEmpty(): void {
  if (escStack.length === 0 && escListener) {
    document.removeEventListener("keydown", escListener);
    escListener = null;
  }
}

export const Modal: FC<
  PropsWithChildren<{ title: ReactNode; onClose: () => void; wide?: boolean }>
> = ({ title, onClose, children, wide }) => {
  const { C } = useC();
  const onCloseRef = useRef(onClose);
  const pointerDownStartedOnBackdropRef = useRef(false);
  onCloseRef.current = onClose;
  useEffect(() => {
    const entry = onCloseRef;
    escStack.push(entry);
    ensureEscListener();
    return () => {
      const i = escStack.lastIndexOf(entry);
      if (i !== -1) escStack.splice(i, 1);
      teardownEscListenerIfEmpty();
    };
  }, []);
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(30,41,71,.45)",
        backdropFilter: "blur(3px)",
      }}
      data-testid="modal-backdrop"
      onMouseDown={(e) => {
        pointerDownStartedOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && pointerDownStartedOnBackdropRef.current) {
          onClose();
        }
        pointerDownStartedOnBackdropRef.current = false;
      }}
    >
      <div
        style={{
          background: C.surface,
          borderRadius: 12,
          boxShadow: C.shL,
          width: wide ? "min(680px, calc(100vw - 32px))" : "min(480px, calc(100vw - 32px))",
          maxHeight: "calc(100vh - 40px)",
          overflow: "auto",
          border: `1px solid ${C.line}`,
        }}
        data-testid="modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 22px",
            borderBottom: `1px solid ${C.lineSoft}`,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: C.navyDeep }}>{title}</h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: 18,
              color: C.slateMut,
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: "18px 22px" }}>{children}</div>
      </div>
    </div>
  );
};

/**
 * ConfirmDialog — replaces window.confirm() with an in-app modal so the
 * UI stays consistent (escape-stack, blur backdrop, themed buttons).
 *
 * Usage:
 *   const [confirm, setConfirm] = useState<{ ... } | null>(null);
 *   ...
 *   setConfirm({ message: "Are you sure?", onConfirm: () => doIt() });
 *   ...
 *   {confirm && <ConfirmDialog {...confirm} onClose={() => setConfirm(null)} />}
 *
 * `destructive` swaps the primary button into the red tone so the user
 * can't miss that the action is irreversible.
 */
export const ConfirmDialog: FC<{
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}> = ({
  title = "Confirm",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onClose,
}) => {
  const { C } = useC();
  return (
    <Modal title={title} onClose={onClose}>
      <div style={{ display: "grid", gap: 16 }}>
        <div style={{ fontSize: 13, color: C.slate, lineHeight: 1.5 }}>{message}</div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn ghost onClick={onClose}>
            {cancelLabel}
          </Btn>
          <button
            type="button"
            onClick={() => {
              void Promise.resolve(onConfirm()).finally(onClose);
            }}
            style={{
              padding: "6px 14px",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 12.5,
              fontWeight: 600,
              color: "#fff",
              background: destructive ? C.r : C.blue,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export const Seg: FC<{
  options: Array<readonly [string, string]>;
  value: string;
  onChange: (v: string) => void;
}> = ({ options, value, onChange }) => {
  const { C } = useC();
  return (
    <div
      style={{
        display: "inline-flex",
        background: C.canvas,
        borderRadius: 6,
        padding: 2,
        gap: 1,
        border: `1px solid ${C.line}`,
      }}
    >
      {options.map(([v, l]) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          style={{
            padding: "3px 11px",
            borderRadius: 4,
            border: "none",
            fontSize: 11.5,
            fontWeight: value === v ? 600 : 500,
            background: value === v ? C.surface : "transparent",
            color: value === v ? C.blue : C.slateMut,
            cursor: "pointer",
            transition: "all .12s",
          }}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Empty: FC<{
  icon?: ReactNode;
  title: ReactNode;
  msg?: ReactNode;
  action?: ReactNode;
}> = ({ icon, title, msg, action }) => {
  const { C } = useC();
  return (
    <div style={{ padding: "48px 24px", textAlign: "center" }}>
      {icon && (
        <div
          style={{ marginBottom: 12, display: "flex", justifyContent: "center", color: C.slateDim }}
        >
          {icon}
        </div>
      )}
      <div style={{ fontSize: 15, fontWeight: 600, color: C.navy, marginBottom: 4 }}>{title}</div>
      {msg && (
        <div
          style={{
            fontSize: 13,
            color: C.slateMut,
            marginBottom: 14,
            maxWidth: 360,
            margin: "0 auto 14px",
          }}
        >
          {msg}
        </div>
      )}
      {action}
    </div>
  );
};

function parseYmKey(k: string): { y: number; m: number } {
  const [y, m] = k.split("-").map(Number);
  return { y: y ?? 0, m: m ?? 0 };
}

function daysInMonthForKey(y: number, m: number): number {
  return new Date(y, m, 0).getDate();
}

/**
 * PeriodPicker — start + end date inputs that emit YYYY-MM month-keys.
 * Verbatim port of designv2/src/ui.jsx PeriodPicker.
 *
 * Initial date strings are derived from the incoming month keys; persistence
 * lives in the parent (App.tsx writes the month-key form to localStorage),
 * so this component is a presentation-only mirror of those props.
 */
function ymKeyToStartDate(key: string) {
  const { y, m } = parseYmKey(key);
  return `${y}-${String(m).padStart(2, "0")}-01`;
}
function ymKeyToEndDate(key: string) {
  const { y, m } = parseYmKey(key);
  return `${y}-${String(m).padStart(2, "0")}-${String(daysInMonthForKey(y, m)).padStart(2, "0")}`;
}

export const PeriodPicker: FC<{
  startKey: string;
  endKey: string;
  onChange: (s: string, e: string) => void;
}> = ({ startKey, endKey, onChange }) => {
  const { C } = useC();

  const [startDate, setStartDate] = useState(() => ymKeyToStartDate(startKey));
  const [endDate, setEndDate] = useState(() => ymKeyToEndDate(endKey));

  // Re-seed local state when the parent's ymKey advances to a NEW month —
  // e.g. after `GET /api/me/range` resolves and the saved range supersedes
  // the default. We track the last prop value we synced FROM so user edits
  // (which set local state without changing the prop in a wired-up parent)
  // don't cause this effect to revert to the prop. Same-month edits also
  // skip re-seed via the ymKey equality check, so picking day-15 within an
  // already-selected month never snaps the day back to the 1st.
  const lastSyncedStartKey = useRef(startKey);
  const lastSyncedEndKey = useRef(endKey);
  useEffect(() => {
    if (startKey !== lastSyncedStartKey.current) {
      lastSyncedStartKey.current = startKey;
      setStartDate(ymKeyToStartDate(startKey));
    }
    if (endKey !== lastSyncedEndKey.current) {
      lastSyncedEndKey.current = endKey;
      setEndDate(ymKeyToEndDate(endKey));
    }
  }, [startKey, endKey]);

  function emitChange(nextStart: string, nextEnd: string) {
    const s = parseYmKey(nextStart);
    const e = parseYmKey(nextEnd);
    const sKey = `${s.y}-${String(s.m).padStart(2, "0")}`;
    const eKey = `${e.y}-${String(e.m).padStart(2, "0")}`;
    if (sKey > eKey) onChange(eKey, sKey);
    else onChange(sKey, eKey);
  }

  function updateStart(d: string) {
    setStartDate(d);
    emitChange(d, endDate);
  }

  function updateEnd(d: string) {
    if (d < startDate) {
      const clamped = ymKeyToEndDate(`${startDate.slice(0, 7)}`);
      setEndDate(clamped);
      emitChange(startDate, clamped);
    } else {
      setEndDate(d);
      emitChange(startDate, d);
    }
  }

  const count = useMemo(() => {
    const s = parseYmKey(startDate);
    const e = parseYmKey(endDate);
    return (e.y - s.y) * 12 + (e.m - s.m) + 1;
  }, [startDate, endDate]);

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 30 }}>
      <span
        style={{
          fontSize: 9.5,
          fontWeight: 700,
          color: C.slateMut,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          fontFamily: "'Source Sans Pro',system-ui,sans-serif",
        }}
      >
        Period
      </span>
      <DatePicker value={startDate} onChange={updateStart} label="" />
      <span style={{ color: C.slateDim, fontSize: 11 }}>→</span>
      <DatePicker value={endDate} onChange={updateEnd} label="" />
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: C.blue,
          background: C.blueTint1,
          padding: "2px 7px",
          borderRadius: 10,
          fontVariantNumeric: "tabular-nums",
          fontFamily: "'Source Sans Pro',system-ui,sans-serif",
        }}
      >
        {count} mo
      </span>
    </div>
  );
};

/**
 * RoleSwitcher — port of designv2/src/ui.jsx RoleSwitcher with two
 * slice-specific additions on top of the verbatim render:
 *   - `canSwitchRole` gate: only admin sees the "View as role (demo)"
 *     list. Non-admins still see their avatar + role label and the
 *     "Log out" item, but cannot impersonate a different role.
 *   - `onLogout` footer (the designv2 prototype has no auth gate so
 *     it didn't need one).
 */
export const RoleSwitcher: FC<{
  role: string;
  setRole: (r: string) => void;
  people: Person[];
  onLogout?: (() => void) | undefined;
  /** Only admins can flip the demo role. Defaults to false to fail closed. */
  canSwitchRole?: boolean | undefined;
  me?: { id: number; name: string } | null | undefined;
  /** Optional dropdown slot rendered between the role list and the logout
   *  footer. Used to host the admin-only Debug menu when DEBUG_TOOLS=1. */
  extraSlot?: ReactNode | undefined;
}> = ({ role, setRole, people, onLogout, canSwitchRole = false, me = null, extraSlot = null }) => {
  const { C } = useC();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    function h(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const labels: Record<string, string> = {
    admin: "Admin",
    lead: "Team Lead",
    pm: "PM",
    accountant: "Accountant",
  };
  const descs: Record<string, string> = {
    admin: "Full access · edit past · manage users",
    lead: "Plan & cost · no project/people setup",
    pm: "Plan future · read actuals · no cost",
    accountant: "Read-only · view costs & margin",
  };
  const repForRole = (r: string) => people.find((p) => p.sysRole === (r as SysRole)) || people[0];
  const rep = me ?? repForRole(role);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: open ? C.blueTint1 : "transparent",
          border: `1px solid ${open ? C.blueTint4 : "transparent"}`,
          borderRadius: 8,
          padding: "3px 6px 3px 3px",
          cursor: "pointer",
          transition: "all .12s",
        }}
      >
        <Av name={rep?.name ?? "User"} sz={28} tone="solid" />
        <div style={{ textAlign: "left", lineHeight: 1.15 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: C.navy,
              fontFamily: "'Source Sans Pro',system-ui,sans-serif",
            }}
          >
            {rep?.name ?? "—"}
          </div>
          <div
            style={{
              fontSize: 10,
              color: C.blue,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontFamily: "'Source Sans Pro',system-ui,sans-serif",
            }}
          >
            {labels[role] ?? role}
          </div>
        </div>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden="true"
          style={{
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .12s",
            marginLeft: 2,
          }}
        >
          <path
            d="M2.5 3.75l2.5 2.5 2.5-2.5"
            stroke={C.slateMut}
            strokeWidth="1.2"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 4,
            background: C.surface,
            borderRadius: 10,
            border: `1px solid ${C.line}`,
            boxShadow: C.shL,
            width: 260,
            padding: 6,
            zIndex: 70,
          }}
        >
          {canSwitchRole && (
            <div
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                color: C.slateMut,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                padding: "6px 10px 4px",
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              View as role (demo)
            </div>
          )}
          {canSwitchRole &&
            (["admin", "lead", "pm", "accountant"] as const).map((r) => {
              const sel = r === role;
              return (
                <button
                  type="button"
                  key={r}
                  role="menuitem"
                  onClick={() => {
                    setRole(r);
                    setOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 6,
                    background: sel ? C.blueTint1 : "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background .1s",
                  }}
                >
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      border: `1.5px solid ${sel ? C.blue : C.line}`,
                      background: sel ? C.blue : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginTop: 1,
                    }}
                  >
                    {sel && (
                      <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true">
                        <path
                          d="M3 6l2 2 4-4"
                          stroke="#fff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                      </svg>
                    )}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: C.navyDeep,
                        fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                      }}
                    >
                      {labels[r]}
                    </div>
                    <div
                      style={{ fontSize: 10.5, color: C.slateMut, marginTop: 2, lineHeight: 1.35 }}
                    >
                      {descs[r]}
                    </div>
                  </div>
                </button>
              );
            })}
          {extraSlot && (
            <>
              <div
                style={{
                  borderTop: `1px solid ${C.lineSoft}`,
                  margin: "6px 4px 4px",
                }}
              />
              {extraSlot}
            </>
          )}
          {onLogout && (
            <>
              <div
                style={{
                  borderTop: `1px solid ${C.lineSoft}`,
                  margin: "6px 4px 4px",
                }}
              />
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onLogout();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 6,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  color: C.slate,
                  fontSize: 12.5,
                  fontWeight: 600,
                  fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                  <path
                    d="M5 3V2a1 1 0 011-1h5a1 1 0 011 1v10a1 1 0 01-1 1H6a1 1 0 01-1-1v-1M8 7H1m0 0l2.5-2.5M1 7l2.5 2.5"
                    stroke={C.slateMut}
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
                Log out
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export function useTableStyles() {
  const { C, density } = useC();
  const d = DENSITIES[density];
  return {
    TH: {
      padding: d.hPad,
      fontSize: 10.5,
      fontWeight: 600,
      color: C.slateMut,
      textTransform: "uppercase" as const,
      letterSpacing: 0.5,
      borderBottom: `1px solid ${C.line}`,
      background: C.canvas,
      whiteSpace: "nowrap" as const,
    },
    TD: {
      padding: `${d.row}px 12px`,
      fontSize: d.fontBody,
      borderBottom: `1px solid ${C.lineSoft}`,
      color: C.slate,
    },
    D: d,
  };
}
